import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Building2, FolderOpen, Users, LogOut, Inbox, Menu, X, LayoutGrid, Settings } from "lucide-react";

const navItems = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutGrid, roles: ["super_admin"] },
  { to: "/admin/tenants", label: "Tenants", icon: Building2, roles: ["super_admin"] },
  { to: "/admin/projects", label: "Projects", icon: FolderOpen, roles: ["super_admin", "admin", "editor", "reseller"] },
  { to: "/admin/leads", label: "Leads", icon: Inbox, roles: ["super_admin", "admin", "editor"] },
  { to: "/admin/users", label: "Users", icon: Users, roles: ["super_admin", "admin"] },
  { to: "/admin/settings", label: "Settings", icon: Settings, roles: ["super_admin"] },
];

export function AdminLayout() {
  const { user, logout, impersonating } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/login"); };
  const visibleItems = navItems.filter(item => item.roles.includes(user?.role));

  return (
    <div
      className="flex h-screen bg-background overflow-hidden"
      style={impersonating ? { paddingTop: "40px" } : undefined}
    >
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar — drawer on mobile, static on desktop */}
      <aside className={`
        z-50 flex flex-col bg-card/95 border-r border-border backdrop-blur-xl
        w-[280px] md:w-60 flex-shrink-0
        fixed md:static top-0 left-0 h-full
        transition-transform duration-300 ease-in-out
        ${sidebarOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full md:translate-x-0"}
      `}>
        {/* Logo row */}
        <div className="h-16 flex items-center px-6 border-b border-border">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <div className="w-7 h-7 rounded-md bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0">
              <div className="w-3 h-3 rounded-sm bg-primary" />
            </div>
            <span className="font-semibold text-sm tracking-wide text-foreground truncate" style={{ fontFamily: "Manrope, sans-serif" }}>
              Visual Platform
            </span>
          </div>
          {/* Close button — mobile only */}
          <button
            className="md:hidden p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors ml-2 flex-shrink-0"
            onClick={() => setSidebarOpen(false)}
            data-testid="sidebar-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {visibleItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase()}`}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 md:py-2.5 rounded-lg text-sm transition-colors duration-150 ${
                  isActive
                    ? "bg-primary/10 text-primary border border-primary/20 font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                }`
              }
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* User footer */}
        <div className="p-3 border-t border-border">
          <div
            className="flex items-center gap-3 px-3 py-3 md:py-2.5 rounded-lg hover:bg-white/5 cursor-pointer group"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold text-foreground flex-shrink-0">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-foreground truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate font-mono">{user?.role}</p>
            </div>
            <LogOut className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-30 flex items-center gap-3 px-4 h-14 border-b border-border bg-card/80 backdrop-blur-xl">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-primary/20 border border-primary/30 flex items-center justify-center">
              <div className="w-2.5 h-2.5 rounded-sm bg-primary" />
            </div>
            <span className="text-sm font-semibold text-foreground" style={{ fontFamily: "Manrope, sans-serif" }}>
              Visual Platform
            </span>
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
